import { FC } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';

import { getSeriesRefererState } from '@rikstv/play-common/src/pages/StackedCoverPage/hooks/seasonHooks';
import { letUserAgentHandleEvent } from '@rikstv/play-common/src/utils/events/event.utils';
import { isOneOf } from '@rikstv/play-common/src/utils/types/typeUtils';
import { playbackActions } from '@rikstv/play-player/src/forces/player.slice';
import { PlayRoundIcon } from '@rikstv/play-player/src/icons';
import { H4, Meta, SubBody } from '@rikstv/shared-components';

import { TvSeriesEpisode } from '../../../forces/tvSeries/tvSeriesDetails.types';
import { UnstyledLink } from '../../../router/Link';
import { commonRoutes } from '../../../router/router.path';
import { getImagePackSet } from '../../../utils/imagePack/imagePack.utils';
import { AssetImage } from '../../assetImage/AssetImage';
import { Breakpoints } from '../../grid/breakpoints';
import { EpisodeListItemImageOverlay } from '../../imageOverlays';
import { BroadcastTimePill } from '../../imageOverlays/BroadcastTimeOverlay';
import { ImageOverlayWrapper } from '../../imageOverlays/ImageOverlayWrapper';
import { StackedProviderImage } from '../../providerImage/StackedProviderImage';
import { ClampLines } from '../../truncateText/ClampLines';

import styles from './Episode.module.css';

interface Props {
  episode: TvSeriesEpisode;
  selected: boolean;
}

export const Episode: FC<Props> = ({ episode, selected }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id, image, duration, description, programState, isPlayable, progress } = episode;
  const isUnavailable = isOneOf(programState.type, ['Expired', 'NoAccess']);
  const notInSubscription = isOneOf(programState.type, ['NoAccess']);

  return (
    <UnstyledLink
      to={episode.episodeLink}
      className={classNames('with-keyboard-focus', styles.tvshowEpisodesEpisode, {
        [styles.episodeSelected]: selected,
        [styles.episodeUnavailable]: isUnavailable,
        [styles.notInSubscription]: notInSubscription,
      })}
      onClick={event => {
        if (!letUserAgentHandleEvent(event)) {
          event.preventDefault();
          // store context in state for series back link
          navigate(episode.episodeLink, { state: getSeriesRefererState() });
        }
      }}>
      <div className={styles.episodeContent}>
        <div className={styles.episodeImageContainer}>
          <AssetImage
            blackWhite={notInSubscription}
            imageSet={getImagePackSet({
              url: image,
              variations: [
                { width: 600, breakpoint: Breakpoints.XS },
                { width: 460, breakpoint: Breakpoints.DEFAULT },
              ],
            })}
            alt=""
          />

          <StackedProviderImage variants={episode.logos} />
          <EpisodeListItemImageOverlay
            vodProgress={progress?.percent}
            duration={duration}
            programState={programState}
          />
          {isPlayable && (
            <ImageOverlayWrapper displayChildrenOnHover={!selected} centerContent>
              <button
                tabIndex={!isPlayable ? -1 : 0}
                className={styles.episodeButton}
                aria-label="Spill av"
                title="Spill av"
                onClick={() => {
                  if (notInSubscription === false) {
                    dispatch(playbackActions.requestAutoPlay());
                    // if we're already on this asset, scroll to top
                    if (commonRoutes.watchAsset({ id }) === window.location.pathname) {
                      window.scrollTo(0, 0);
                      // TODO: also play? Must handle all states (not started, continue watching, paused in player) and know if asset is external...
                    }
                  }
                }}>
                <PlayRoundIcon iconSize={40} />
              </button>
            </ImageOverlayWrapper>
          )}
        </div>
        <div className={styles.episodeTextContainer}>
          {programState.type === 'Planned' && (
            <BroadcastTimePill className={styles.pillOnMobile} label={programState.label} />
          )}
          <ClampLines textStyle={H4} lines={1}>
            {episode.title}
          </ClampLines>
          <Meta className="tvshow-episodes__episode__meta"> {episode.subtitle}</Meta>
          <ClampLines textStyle={SubBody} lines={3} className={styles.episodeDescription}>
            {description}
          </ClampLines>
        </div>
      </div>
    </UnstyledLink>
  );
};
